import React from 'react';
import { VCardContact, ContactActionHandlers } from '../types';
import { 
    UserIcon, 
    PhoneIcon, 
    EnvelopeIcon, 
    BuildingOfficeIcon,
    DocumentArrowDownIcon,
    ChatBubbleLeftRightIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { formatPhoneNumber, parseVCard } from '../utils';

interface ContactCardProps extends ContactActionHandlers {
    contact: VCardContact;
    compact?: boolean;
}

const ContactCard: React.FC<ContactCardProps> = ({
    contact,
    compact = false,
    onCall,
    onMessage,
    onEmail,
    onSaveContact,
    onViewContact,
    onCreateChat
}) => {
    console.log('🔍 ContactCard received contact:', contact);
    
    let processedContact = contact;
    if (typeof contact === 'string') {
        console.log('🔄 Emergency parsing vCard string in ContactCard');
        processedContact = parseVCard(contact);
    } else if (contact && typeof contact.name === 'string' && contact.name.startsWith('BEGIN:VCARD')) {
        console.log('🔄 Emergency parsing vCard from name field');
        processedContact = parseVCard(contact.name);
    }
    
    console.log('✅ Processed contact for display:', processedContact);

    const handleCall = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (processedContact.phone && onCall) {
            onCall(processedContact.phone);
        }
    };

    const handleCreateChat = (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('🔍 ContactCard Debug - Create chat clicked:', {
            originalContact: contact,
            processedContact: processedContact,
            contactPhone: processedContact.phone,
            phoneType: typeof processedContact.phone,
            onCreateChat: !!onCreateChat
        });
        
        if (!processedContact.phone) {
            console.error('❌ No phone number found in processed contact:', processedContact);
            alert('Не удалось создать чат: отсутствует номер телефона');
            return;
        }
        
        const cleanedPhone = processedContact.phone.replace(/\D/g, '');
        console.log('📞 Cleaned phone number:', cleanedPhone);
        
        if (cleanedPhone && onCreateChat) {
            console.log('✅ Calling onCreateChat with:', cleanedPhone, processedContact.name);
            onCreateChat(cleanedPhone, processedContact.name);
        } else {
            console.error('❌ Cannot create chat:', {
                cleanedPhone: cleanedPhone,
                hasOnCreateChat: !!onCreateChat,
                cleanedPhoneLength: cleanedPhone?.length
            });
            alert('Не удалось создать чат: неверный номер телефона');
        }
    };

    const handleEmail = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (processedContact.email && onEmail) {
            onEmail(processedContact.email);
        }
    };

    const handleSaveContact = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onSaveContact && processedContact) {
            onSaveContact(processedContact);
        }
    };

    const handleViewDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onViewContact && processedContact) {
            onViewContact(processedContact);
        }
    };

    // Main card click - redirect to chat
    const handleCardClick = () => {
        console.log('🔄 ContactCard main click - processed contact:', processedContact);
        
        if (processedContact.phone && onCreateChat) {
            const cleanedPhone = processedContact.phone.replace(/\D/g, '');
            console.log('🔄 Main click - creating chat with:', cleanedPhone);
            onCreateChat(cleanedPhone, processedContact.name);
        } else {
            console.log('🔄 Main click - cannot create chat, missing phone or handler');
        }
    };

    const displayName = processedContact.name && !processedContact.name.startsWith('BEGIN:VCARD') 
        ? processedContact.name 
        : 'Контакт';

    if (!processedContact || (!processedContact.name && !processedContact.phone)) {
        return (
            <div className="flex items-center space-x-2 p-2 bg-gray-100 rounded-lg max-w-xs">
                <UserIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                <div className="flex-grow overflow-hidden">
                    <p className="text-sm font-medium">Контакт</p>
                    <p className="text-xs text-gray-700">Данные недоступны</p>
                </div>
            </div>
        );
    }

    if (compact) {
        return (
            <div 
                className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={handleCardClick}
            >
                <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {displayName}
                    </h4>
                    {processedContact.phone && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {formatPhoneNumber(processedContact.phone)}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div 
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden cursor-pointer hover:shadow-md transition-shadow max-w-xs"
            onClick={handleCardClick}
        >
            <div className="flex items-center space-x-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                        <UserIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {displayName}
                    </h3>
                    {processedContact.title && (
                        <p className="text-xs text-gray-600 dark:text-gray-300 truncate">
                            {processedContact.title}
                        </p>
                    )}
                    {processedContact.org && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {processedContact.org}
                        </p>
                    )}
                </div>
            </div>

            <div className="p-3 space-y-2">
                {processedContact.phone && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <PhoneIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-700 dark:text-gray-300 font-medium truncate">
                                {formatPhoneNumber(processedContact.phone)}
                            </span>
                        </div>
                        <div className="flex space-x-1 ml-2">
                            <button
                                onClick={handleCall}
                                className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors flex-shrink-0"
                                title="Позвонить"
                            >
                                <PhoneIcon className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                )}
                
                {processedContact.email && (
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                            <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-700 dark:text-gray-300 truncate">
                                {processedContact.email}
                            </span>
                        </div>
                        <button
                            onClick={handleEmail}
                            className="p-1 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors flex-shrink-0 ml-2"
                            title="Отправить email"
                        >
                            <EnvelopeIcon className="w-3 h-3" />
                        </button>
                    </div>
                )}
            </div>

            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between items-center space-x-2">
                    <button
                        onClick={handleViewDetails}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                        title="Просмотреть детали контакта"
                    >
                        <InformationCircleIcon className="w-3 h-3" />
                        <span>Детали</span>
                    </button>

                    <button
                        onClick={handleSaveContact}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors"
                        title="Сохранить контакт"
                    >
                        <DocumentArrowDownIcon className="w-3 h-3" />
                        <span>Сохранить</span>
                    </button>

                    <button
                        onClick={handleCreateChat}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded transition-colors"
                        title="Написать сообщение"
                    >
                        <ChatBubbleLeftRightIcon className="w-3 h-3" />
                        <span>Написать</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContactCard;